import { 
  ServiceSelection, 
  ServiceSelectionCreate, 
  ServiceSelectionUpdate, 
  UpdateSelectionsRequest 
} from '../../types/planningTypes';


export const validateSelections = (selections: ServiceSelection[]): boolean => {
  return selections.every(selection => 
    selection.song_id && // Must have a song selected
    selection.placement && // Must have placement text
    selection.order_number > 0 // Must have positive order number
  );
};

export const validateSequence = (selections: ServiceSelection[]): boolean => {
  const orderNumbers = selections.map(s => s.order_number);
  for (let i = 0; i < orderNumbers.length; i++) {
    if (orderNumbers[i] !== i + 1) {
      return false;
    }
  }
  return true;
};

export const resequenceSelections = (selections: ServiceSelection[]): ServiceSelection[] => {
  // The current array order IS the correct order after drag and drop
  // We just need to update order_number to match this order
  return selections.map((selection, index) => {
    // Make a new object to avoid mutating the original
    return {
      ...selection,
      order_number: index + 1  // Assign new order number based on array position
    };
  });
};

export const createNewSelection = (
  serviceId: number,
  currentSelections: ServiceSelection[]
): ServiceSelection => {
  const maxOrder = currentSelections.length ? 
    Math.max(...currentSelections.map(s => s.order_number)) : 0;
  
  return {
    service_id: serviceId,
    placement: '',
    order_number: maxOrder + 1,
    song_title: '',
    performance_type: 'Congregational Hymn',
  };
};

export const prepareUpdateRequest = (
  originalSelections: ServiceSelection[],
  currentSelections: ServiceSelection[]
): UpdateSelectionsRequest => {
  console.log('[prepareUpdateRequest] Starting comparison:', {
    originalCount: originalSelections.length,
    currentCount: currentSelections.length
  });

  const recordsToAdd: ServiceSelectionCreate[] = [];
  const recordsToUpdate: ServiceSelectionUpdate[] = [];
  const recordsToDelete: number[] = [];

  // Find deleted records
  originalSelections.forEach(original => {
    if (original.service_selection_id) {
      const stillExists = currentSelections.some(
        curr => curr.service_selection_id === original.service_selection_id
      );

      if (!stillExists) {
        console.log('[prepareUpdateRequest] Found record to delete:', {
          service_selection_id: original.service_selection_id,
          song_title: original.song_title
        });
        recordsToDelete.push(original.service_selection_id);
      }
    }
  });

  // Handle adds and updates
  currentSelections.forEach(current => {
    // Skip invalid records
    if (!current.song_id) {
      console.log('[prepareUpdateRequest] Skipping record without song_id:', current);
      return;
    }

    if (!current.service_selection_id) {
      // New record
      console.log('[prepareUpdateRequest] Adding new record:', {
        service_id: current.service_id,
        song_id: current.song_id,
        placement: current.placement,
        order_number: current.order_number
      });

      recordsToAdd.push({
        service_id: current.service_id,
        song_id: current.song_id,
        version_id: current.version_id ?? null,
        placement: current.placement,
        order_number: current.order_number,
        performance_type: current.performance_type
      });
    } else {
      // Existing record - check if updated
      const original = originalSelections.find(
        orig => orig.service_selection_id === current.service_selection_id
      );
      
      if (original && (
        original.song_id !== current.song_id ||
        original.version_id !== current.version_id ||
        original.placement !== current.placement ||
        original.order_number !== current.order_number ||
        original.performance_type !== current.performance_type
      )) {
        console.log('[prepareUpdateRequest] Updating existing record:', {
          id: current.service_selection_id,
          changes: {
            song_id: { from: original.song_id, to: current.song_id },
            version_id: { from: original.version_id, to: current.version_id },
            placement: { from: original.placement, to: current.placement },
            order: { from: original.order_number, to: current.order_number }
          }
        });

        recordsToUpdate.push({
          service_selection_id: current.service_selection_id,
          service_id: current.service_id,
          song_id: current.song_id,
          version_id: current.version_id ?? null,
          placement: current.placement,
          order_number: current.order_number,
          performance_type: current.performance_type
        });
      }
    }
  });

  const request: UpdateSelectionsRequest = {};
  
  if (recordsToAdd.length > 0) {
    request.recordsToAdd = recordsToAdd;
  }
  if (recordsToUpdate.length > 0) {
    request.recordsToUpdate = recordsToUpdate;
  }
  if (recordsToDelete.length > 0) {
    request.recordsToDelete = recordsToDelete;
  }

  console.log('[prepareUpdateRequest] Final request:', {
    addCount: recordsToAdd.length,
    updateCount: recordsToUpdate.length,
    deleteCount: recordsToDelete.length,
    request
  });

  return request;
};